import React from 'react';
import { useFormik } from 'formik';
import { TextField as TextInput } from '@material-ui/core';
import PrimaryButton from 'src/components/PrimaryButton/PrimaryButton.component';
import { t } from 'src/i18n';
import Routes from '../../Navigator/Navigator.route';
import * as Yup from 'yup';
import {
  deleteAllCookie,
  setCookie,
  getCookie,
  isKeyPressEnterEvent,
  parseJSON
} from 'src/utils/common.util';

import { useCookies } from 'react-cookie';

import API from 'src/api/api';
import ErrorPopup from 'src/components/ErrorPopup/ErrorPopup';
import MethodSelectionPopup from './MethodSelection.component';
import { APP_CONSTANT } from 'src/constants';
import {
  useStyles,
  FormLabel,
  TextInBoldRegister,
  LinkWrapper,
  LinkWithoutMargin
} from '../Login.style';
import ErrorMessageWithIcon from 'src/components/ErrorMessageWithIcon/ErrorMessageWithIcon.component';
import LoadingContainer from 'src/components/LoadingContainer/LoadingContainer';
import { getUserInfo } from '../utils';
import { useHistory } from 'react-router-dom';

interface IProps {
  setStepLogin(step: number): void;
  setVerificationMethod(method: string): void;
}

const LoginWithEmailForm: React.FC<IProps> = (props) => {
  const classes = useStyles();
  const { setStepLogin, setVerificationMethod } = props;
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const [showErrorPopup, setShowErrorPopup] = React.useState<boolean>(false);
  const [showMethodPopup, setShowMethodPopup] = React.useState<boolean>(false);
  const [methodValue, setMethodValue] = React.useState<string>(
    APP_CONSTANT.SMS
  );
  const [phoneNumber, setPhoneNumber] = React.useState<string>('');
  const [emailError, setEmailError] = React.useState<string>('');
  const [passwordError, setPasswordError] = React.useState<string>('');
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [cookie, setCookieFn] = useCookies([]);
  const history = useHistory();

  const handleSelectMethod = (event) => {
    setMethodValue(event.target.value);
  };

  const resetErrorMessage = (_err) => {
    deleteAllCookie();
  };

  const sendMethodConfirmation = async (hasPhoneNumber) => {
    try {
      const accountId = (parseJSON('tenantInfo') as any).accountId;
      const authId = getCookie('authId');
      const method = hasPhoneNumber ? methodValue : APP_CONSTANT.EMAIL;

      const response = await API().post('/auth/v1/public/send-otp', {
        authId,
        accountId,
        type: method
      });
      if (response.status !== 200) {
        return;
      }

      setCookie('expiredDateTime', response.data.expiredDateTime, {
        duration: 24
      });
      setCookie('remainingTime', response.data.remainingTime, { duration: 24 });

      setVerificationMethod(method);
      setStepLogin(2);
    } catch (error) {
      if (error.response.status) {
        setErrorMessage(error.response.data.message);
        setShowErrorPopup(true);
        setIsLoading(false);
      }
    }
  };

  const {
    handleSubmit,
    values,
    handleChange,
    handleBlur,
    touched,
    errors,
    setFieldValue
  } = useFormik({
    initialValues: {
      email: '',
      password: ''
    },

    onSubmit: async (val) => {
      setIsLoading(true);
      try {
        const accountId = (parseJSON('tenantInfo') as any).accountId;
        const response = await API().post('/auth/v1/public/oauth/token', {
          ...val,
          accountId,
          grantType: 'password'
        });
        if (response.status !== 200) {
          return;
        }

        if (response.data.token && response.data.contactId) {
          setCookie('token', response.data.token, { duration: 24 });
          await getUserInfo(response, setCookieFn, resetErrorMessage, history);
          setIsLoading(false);
        } else {
          setCookie('authId', response.data.authId, { duration: 24 });
          if (response.data.phoneNumber) {
            setPhoneNumber(response.data.phoneNumber);
            setShowMethodPopup(true);
            setIsLoading(false);
          } else {
            sendMethodConfirmation(false);
          }
        }
      } catch (error) {
        if (error.response.status) {
          setErrorMessage(error.response.data.message);
          setShowErrorPopup(true);
          setIsLoading(false);
          
          return;
        }

        setErrorMessage(error);
        setShowErrorPopup(true);
        setIsLoading(false);
      }
    },

    validationSchema: Yup.object().shape({
      email: Yup.string()
        .email(t('signIn.error.format.email'))
        .required(t('signIn.error.required.email')),

      password: Yup.string().required(t('signIn.error.required.password'))
    })
  });

  const directLocation = (pathName) => {
    return pathName;
  };

  const handleKeydown = (event) => {
    if (isKeyPressEnterEvent(event)) handleSubmit();

    return false;
  };

  const trimWhenBlur = (fieldName) => (e) => {
    const trimValue = e.target.value.trim();

    setFieldValue(fieldName, trimValue, true);
  };

  React.useEffect(() => {
    if (!!errors.email && !!touched.email) {
      setEmailError(errors.email);
    } else {
      setEmailError('');
    }

    if (!!errors.password && !!touched.password) {
      setPasswordError(errors.password);
    } else {
      setPasswordError('');
    }
  }, [errors, touched]);

  return (
    <LoadingContainer isLoading={isLoading} isOverlay={true}>
      <>
        <form autoComplete="off">
          <div className="login__input-group">
            <div style={{ display: 'none' }}>
              <input
                type="text"
                id="PreventChromeAutocomplete"
                name="PreventChromeAutocomplete"
                autoComplete="address-level4"
              />
            </div>

            <div className={classes.mb30}>
              <FormLabel>{t('signIn.label.email')}</FormLabel>
              <TextInput
                className={classes.input}
                onChange={handleChange}
                onKeyDown={handleKeydown}
                name="email"
                value={values.email}
                type="text"
                label={null}
                autoComplete="off"
                variant="outlined"
                placeholder={t('signIn.placeholder.email')}
                inputProps={{
                  style: { fontSize: 20 },
                  onBlur: trimWhenBlur('email')
                }}
                onBlur={handleBlur}
                error={!!errors.email && !!touched.email}
              />
              {emailError && <ErrorMessageWithIcon message={emailError} />}
            </div>

            <div>
              <FormLabel>{t('signIn.label.password')}</FormLabel>
              <TextInput
                className={classes.input}
                onChange={handleChange}
                onKeyDown={handleKeydown}
                name="password"
                value={values.password}
                type="password"
                autoComplete="off"
                label={null}
                variant="outlined"
                placeholder={t('signIn.placeholder.password')}
                inputProps={{ maxLength: 50, style: { fontSize: 20 } }}
                onBlur={handleBlur}
                error={!!errors.password && !!touched.password}
              />
              {passwordError && (
                <ErrorMessageWithIcon message={passwordError} />
              )}
            </div>
          </div>
        </form>
        <div className={classes.forgotPasswordWrapper}>
          <LinkWrapper to={directLocation(Routes.ForgotPassword)}>
            {t('signIn.forgotPassword')}
          </LinkWrapper>
        </div>

        <ErrorPopup
          showErrorPopup={showErrorPopup}
          setShowErrorPopup={setShowErrorPopup}
          errorMessage={errorMessage}
        />

        <MethodSelectionPopup
          showMethodPopup={showMethodPopup}
          setShowMethodPopup={setShowMethodPopup}
          handleSelectMethod={handleSelectMethod}
          value={methodValue}
          phoneNumber={phoneNumber}
          email={values.email}
          sendMethod={sendMethodConfirmation}
        />

        <PrimaryButton
          onClick={handleSubmit as any}
          title={t('signIn.button.signIn')}
          variant="contained"
          titleClassName="login"
          isShowIcon={true}
        />

        <TextInBoldRegister>
          {t('signIn.dontHaveAccount')}{' '}
          <LinkWithoutMargin to={directLocation(Routes.Register)}>
            {t('signIn.register')}
          </LinkWithoutMargin>
        </TextInBoldRegister>
      </>
    </LoadingContainer>
  );
};

export default LoginWithEmailForm;
